import { createContext, useContext, type ReactNode } from 'react';
import type { GmailAuth } from './useGmailToken';

/**
 * ONE Gmail authorisation for the whole TA Action Board.
 *
 * The grant itself was always page-wide (useGmailToken persists it in
 * sessionStorage), but every tab used to mount its own hook instance, so a
 * connect made in one tab only reached the others on remount — and there was
 * no place to connect that wasn't inside a specific tab. The board now mounts
 * useGmailToken ONCE (TAActionBoardView), shares the live instance here, and
 * shows the connect state in the header where every tab can see it.
 *
 * Consumers: the Requests tab (inbox), PanelSuggestion (calendars via Drive),
 * and the archive flow (rejection email as a Gmail draft in YOUR mailbox).
 */
const GmailConnectionContext = createContext<GmailAuth | null>(null);

export function GmailConnectionProvider({
  value,
  children,
}: {
  value: GmailAuth;
  children: ReactNode;
}) {
  return <GmailConnectionContext.Provider value={value}>{children}</GmailConnectionContext.Provider>;
}

export function useGmailConnection(): GmailAuth {
  const ctx = useContext(GmailConnectionContext);
  if (!ctx) {
    throw new Error('useGmailConnection must be used inside GmailConnectionProvider');
  }
  return ctx;
}

/**
 * The same grant, but tolerant of there being no provider.
 *
 * For components that are also rendered on their own — in a test, or one day
 * outside the board — where "Gmail is not connected" is a perfectly good answer
 * and throwing would be worse than degrading to the manual path.
 */
export function useOptionalGmailConnection(): GmailAuth | null {
  return useContext(GmailConnectionContext);
}
